import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/AdminEvents.css";

import {
  EVENTS_API,
  EVENT_IMAGE_BASE,
  ABOUT_API,
  ABOUT_UPLOAD_API,
  getImageUrl,
} from "../config/api";
import { showConfirm } from "../utils/customDialog";

function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [activeTab, setActiveTab] = useState("all-events"); // "all-events" | "add-event" | "awards"
  const [activeCategoryTab, setActiveCategoryTab] = useState("upcoming"); // "upcoming" | "previous"

  // Awards State
  const [awardList, setAwardList] = useState([]);
  const [savingAwards, setSavingAwards] = useState(false);

  const initialForm = {
    title: "",
    date: "",
    location: "",
    category: "",
    customCategory: "",
    type: "",
    customType: "",
    price: "0",
    description: "",
    image: null,
  };

  const [form, setForm] = useState(initialForm);
  const popupRef = useRef(null);

  // ================= POPUP NOTIFICATION =================
  const showPopup = (msg, type = "success") => {
    if (popupRef.current) popupRef.current.remove();

    const div = document.createElement("div");
    popupRef.current = div;

    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === "error" ? "#dc2626" : "#5d1d2b"};
        color: #fff;
        padding: 14px 18px;
        border-radius: 10px;
        z-index: 99999;
        box-shadow: 0 10px 25px rgba(93, 29, 43, 0.25);
        font-size: 14px;
        font-weight: 600;
        max-width: 320px;
        animation: slideIn 0.3s ease;
      ">
        ${msg}
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      </style>
    `;

    document.body.appendChild(div);

    setTimeout(() => {
      div.remove();
      popupRef.current = null;
    }, 2500);
  };

  // ================= UNLINK OTHER ADMIN CSS =================
  

  // ================= FETCH EVENTS & AWARDS =================
  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${EVENTS_API}?action=getEvents`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setEvents(data);
      } else {
        setEvents([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setEvents([]);
    }
  }, []);

  const fetchAwards = useCallback(async () => {
    try {
      const res = await fetch(ABOUT_API);
      const data = await res.json();
      if (data.status === "success") {
        setAwardList(data.data?.award_section || []);
      }
    } catch (err) {
      console.error("Fetch awards error:", err);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchAwards();
  }, [fetchEvents, fetchAwards]);

  // ================= INPUT CHANGE =================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ================= IMAGE HANDLER =================
  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setForm((prev) => ({
      ...prev,
      image: file,
    }));

    setImagePreview(URL.createObjectURL(file));
  };

  // ================= RESET FORM =================
  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setImagePreview("");
  };

  // ================= SUBMIT EVENT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    const finalCategory = form.category === "custom" ? form.customCategory : form.category;
    const finalType = form.type === "custom" ? form.customType : form.type;

    formData.append("title", form.title);
    formData.append("date", form.date);
    formData.append("location", form.location);
    formData.append("category", finalCategory);
    formData.append("type", finalType);
    formData.append("price", "0");
    formData.append("description", form.description);

    if (form.image) {
      formData.append("image", form.image);
    }

    formData.append("action", editingId ? "updateEvent" : "addEvent");

    if (editingId) {
      formData.append("id", editingId);
    }

    try {
      const res = await fetch(EVENTS_API, {
        method: "POST",
        body: formData,
      });

      const result = await res.json();

      if (result?.success || result) {
        showPopup(editingId ? "Event Updated Successfully" : "Event Added Successfully");
        resetForm();
        fetchEvents();
        setActiveTab("all-events");
      } else {
        showPopup("Operation failed", "error");
      }
    } catch (err) {
      console.error("Submit error:", err);
      showPopup("Server error", "error");
    }
  };

  // ================= EDIT EVENT =================
  const handleEdit = (event) => {
    setForm({
      title: event.title || "",
      date: event.date || "",
      location: event.location || "",
      category: event.category || "",
      customCategory: "",
      type: event.type || "",
      customType: "",
      price: event.price || "",
      description: event.description || "",
      image: null,
    });

    setImagePreview(event.image ? `${EVENT_IMAGE_BASE}${event.image}` : "");
    setEditingId(event.id);
    setActiveTab("add-event");
  };

  // ================= DELETE EVENT =================
  const handleDelete = async (id) => {
    const ok = await showConfirm({
      title: "Delete Event",
      message: "Are you sure you want to delete this event?",
      subMessage: "This will permanently remove the event from listings and bookings.",
      confirmText: "Delete Event",
      type: "danger",
    });
    if (!ok) return;

    try {
      const res = await fetch(`${EVENTS_API}?action=deleteEvent&id=${id}`);
      const result = await res.json();

      if (result?.success || result) {
        fetchEvents();
        showPopup("Event Deleted");
      } else {
        showPopup("Delete failed", "error");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showPopup("Error deleting event", "error");
    }
  };

  // ================= AWARDS MANAGEMENT FUNCTIONS =================
  const handleAwardImageUpload = async (file, index, field) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await fetch(ABOUT_UPLOAD_API, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.status === "success") {
        const copy = [...awardList];
        copy[index][field] = data.path;
        setAwardList(copy);
        showPopup(`Award photo uploaded`);
      } else {
        showPopup("Image upload failed", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Upload error", "error");
    }
  };

  const handleRemoveAwardImage = (index, field) => {
    const copy = [...awardList];
    copy[index][field] = "";
    setAwardList(copy);
  };

  const handleAddAward = () => {
    const newAward = {
      title: "",
      date: "",
      content: "",
      image1: "",
      image2: "",
      image3: "",
    };
    setAwardList((prev) => [...prev, newAward]);
    showPopup("New award draft added. Fill in details and click 'Save All Awards'.");
  };

  const handleDeleteAward = async (index, item) => {
    const ok = await showConfirm({
      title: "Delete Award Entry",
      message: "Are you sure you want to permanently delete this award / felicitation?",
      subMessage: "This will remove the award from the Events page and Home showcase.",
      confirmText: "Delete Award",
      type: "danger",
    });
    if (!ok) return;

    if (item && item.id) {
      try {
        const res = await fetch(`${ABOUT_API}/award_section/${item.id}`, {
          method: "DELETE",
        });
        const data = await res.json();
        if (data.status === "success") {
          setAwardList((prev) => prev.filter((_, i) => i !== index));
          showPopup("Award permanently deleted from database");
          return;
        } else {
          showPopup(data.message || "Failed to delete award", "error");
          return;
        }
      } catch (err) {
        console.error("Delete error:", err);
        showPopup("Server error while deleting", "error");
        return;
      }
    }

    setAwardList((prev) => prev.filter((_, i) => i !== index));
    showPopup("Award entry removed");
  };

  const handleSaveAwards = async () => {
    setSavingAwards(true);
    try {
      const res = await fetch(ABOUT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ award_section: awardList }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Awards & Felicitations Saved Successfully!");
        fetchAwards();
      } else {
        showPopup(data.message || "Failed to save awards", "error");
      }
    } catch (err) {
      console.error("Save awards error:", err);
      showPopup("Server error while saving awards", "error");
    } finally {
      setSavingAwards(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingEvents = events.filter((event) => {
    if (!event.date) return false;
    const eventDate = new Date(event.date);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
  });

  const previousEvents = events.filter((event) => {
    if (!event.date) return false;
    const eventDate = new Date(event.date);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate < today;
  });

  const eventsToDisplay = activeCategoryTab === "upcoming" ? upcomingEvents : previousEvents;

  return (
    <div className="admin-container events-admin-container">
      {/* Header with Dashboard Metrics */}
      <div className="events-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-calendar2-event"></i> Events & Awards Manager
          </h1>
          <p>Create, update, and manage Vedic events, festivals, felicitations, and prestigious awards.</p>
        </div>

        <div className="events-metrics-grid">
          <div className="metric-card" onClick={() => setActiveTab("all-events")} style={{ cursor: "pointer" }}>
            <div className="metric-icon events"><i className="bi bi-calendar-event"></i></div>
            <div className="metric-info">
              <span className="metric-value">{events.length}</span>
              <span className="metric-label">Total Events</span>
            </div>
          </div>
          <div className="metric-card" onClick={() => setActiveTab("awards")} style={{ cursor: "pointer" }}>
            <div className="metric-icon online" style={{ background: "rgba(212, 175, 55, 0.15)", color: "#b88a1b" }}>
              <i className="bi bi-trophy-fill"></i>
            </div>
            <div className="metric-info">
              <span className="metric-value">{awardList.length}</span>
              <span className="metric-label">Awards & Honors</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon offline"><i className="bi bi-geo-alt"></i></div>
            <div className="metric-info">
              <span className="metric-value">{events.filter(e => e.type?.toLowerCase() === "offline").length}</span>
              <span className="metric-label">Offline</span>
            </div>
          </div>
        </div>
      </div>

      {/* PAGE TABS NAVIGATION */}
      <div className="events-tabs">
        <button
          type="button"
          className={`events-tab-btn ${activeTab === "all-events" ? "active" : ""}`}
          onClick={() => setActiveTab("all-events")}
        >
          <i className="bi bi-grid-3x3-gap"></i>
          <span className="tab-text-container">
            <strong>Events Catalog</strong>
            <span className="tab-subtext">View upcoming & previous events</span>
          </span>
          <span className="badge-count">{events.length}</span>
        </button>

        <button
          type="button"
          className={`events-tab-btn ${activeTab === "add-event" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("add-event");
            if (!editingId) resetForm();
          }}
        >
          <i className="bi bi-plus-circle"></i>
          <span className="tab-text-container">
            <strong>{editingId ? "Edit Event" : "Add New Event"}</strong>
            <span className="tab-subtext">{editingId ? "Update event details" : "Publish new event"}</span>
          </span>
        </button>

        <button
          type="button"
          className={`events-tab-btn ${activeTab === "awards" ? "active" : ""}`}
          onClick={() => setActiveTab("awards")}
        >
          <i className="bi bi-award-fill" style={{ color: "#d4af37" }}></i>
          <span className="tab-text-container">
            <strong>Awards & Felicitations</strong>
            <span className="tab-subtext">Manage honors & ceremony photos</span>
          </span>
          <span className="badge-count" style={{ background: "#d4af37", color: "#000" }}>{awardList.length}</span>
        </button>
      </div>

      {/* ================= ADD / EDIT EVENT FORM ================= */}
      {activeTab === "add-event" && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <input
            name="title"
            placeholder="Event Title"
            value={form.title}
            onChange={handleChange}
            required
          />

          <input
            type="date"
            name="date"
            value={form.date}
            onChange={handleChange}
            required
          />

          <input
            name="location"
            placeholder="Location"
            value={form.location}
            onChange={handleChange}
            required
          />

          {/* CATEGORY */}
          <select name="category" value={form.category} onChange={handleChange}>
            <option value="">Select Category</option>
            <option value="veda">Veda Parayanam</option>
            <option value="upanyasam">Upanyasam / Discourse</option>
            <option value="yagna">Yagna</option>
            <option value="festival">Festival</option>
            <option value="custom">+ Add New Category</option>
          </select>

          {form.category === "custom" && (
            <input
              name="customCategory"
              placeholder="Enter new category"
              value={form.customCategory}
              onChange={handleChange}
            />
          )}

          {/* TYPE */}
          <select name="type" value={form.type} onChange={handleChange}>
            <option value="">Select Type</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="custom">+ Add New Type</option>
          </select>

          {form.type === "custom" && (
            <input
              name="customType"
              placeholder="Enter new type"
              value={form.customType}
              onChange={handleChange}
            />
          )}

          <textarea
            name="description"
            placeholder="Event Description"
            value={form.description}
            onChange={handleChange}
          />

          <input type="file" onChange={handleImage} />

          {imagePreview && (
            <img src={imagePreview} alt="preview" className="image-preview" />
          )}

          <div className="form-actions">
            {editingId && (
              <button type="submit" className="save-changes-btn">
                Save Changes
              </button>
            )}
            <button type="submit">
              {editingId ? "Update Event" : "Add Event"}
            </button>

            {editingId && (
              <button type="button" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {/* ================= EVENTS LIST ================= */}
      {activeTab === "all-events" && (
        <>
          <div className="admin-events-subtabs">
            <button
              type="button"
              className={`subtab-btn ${activeCategoryTab === "upcoming" ? "active" : ""}`}
              onClick={() => setActiveCategoryTab("upcoming")}
            >
              Upcoming Events ({upcomingEvents.length})
            </button>
            <button
              type="button"
              className={`subtab-btn ${activeCategoryTab === "previous" ? "active" : ""}`}
              onClick={() => setActiveCategoryTab("previous")}
            >
              Previous Events ({previousEvents.length})
            </button>
          </div>

          <div className="admin-events-list">
            {eventsToDisplay.length > 0 ? (
              eventsToDisplay.map((event) => (
                <div key={event.id} className="admin-event-card">
                  <img
                    src={`${EVENT_IMAGE_BASE}${event.image}`}
                    alt={event.title}
                  />

                  <div className="content">
                    <h3>{event.title}</h3>
                    <p><i className="bi bi-calendar3 me-1"></i> {event.date}</p>
                    <p><i className="bi bi-geo-alt me-1"></i> {event.location}</p>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" }}>
                      <span className="tag">{event.category || "General"}</span>
                      <span className="tag">{event.type || "Event"}</span>
                    </div>
                  </div>

                  <div className="actions">
                    <button onClick={() => handleEdit(event)}>Edit</button>
                    <button className="delete-btn" onClick={() => handleDelete(event.id)}>Delete</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-events">No events available in this section.</div>
            )}
          </div>
        </>
      )}

      {/* ================= AWARDS & FELICITATIONS TAB ================= */}
      {activeTab === "awards" && (
        <div className="admin-awards-section-wrap">
          <div className="admin-section-header-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px", flexWrap: "wrap", gap: "15px" }}>
            <div>
              <h2 style={{ color: "#5d1d2b", margin: 0, fontWeight: "800", fontSize: "22px", display: "flex", alignItems: "center", gap: "10px" }}>
                <i className="bi bi-trophy-fill" style={{ color: "#d4af37" }}></i> Prestigious Awards & Felicitations
              </h2>
              <p style={{ color: "#64748b", margin: "4px 0 0 0", fontSize: "14px" }}>
                Manage all awards and honours displayed on the Events and Home pages.
              </p>
            </div>

            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <button
                type="button"
                onClick={handleAddAward}
                className="admin-btn-gold"
                style={{ background: "#5d1d2b", color: "#fff", border: "1.5px solid #d4af37", padding: "10px 20px", borderRadius: "10px", fontWeight: "700", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}
              >
                <i className="bi bi-plus-circle-fill"></i> Add Award Entry
              </button>

              <button
                type="button"
                onClick={handleSaveAwards}
                disabled={savingAwards}
                style={{ background: "linear-gradient(135deg, #d4af37 0%, #b88a1b 100%)", color: "#3d131c", border: "none", padding: "10px 24px", borderRadius: "10px", fontWeight: "800", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", boxShadow: "0 4px 15px rgba(212, 175, 55, 0.35)" }}
              >
                {savingAwards ? (
                  <><span className="spinner-border spinner-border-sm" role="status"></span> Saving...</>
                ) : (
                  <><i className="bi bi-check2-circle"></i> Save All Awards</>
                )}
              </button>
            </div>
          </div>

          {awardList.length === 0 ? (
            <div className="no-events" style={{ padding: "60px 20px", textAlign: "center" }}>
              <i className="bi bi-trophy" style={{ fontSize: "48px", color: "#d4af37", display: "block", marginBottom: "15px" }}></i>
              <h3>No Awards Configured Yet</h3>
              <p>Click "Add Award Entry" to register your first award.</p>
            </div>
          ) : (
            <div className="admin-awards-cards-list" style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
              {awardList.map((item, index) => (
                <div
                  key={index}
                  className="admin-award-item-card"
                  style={{
                    background: "#ffffff",
                    border: "1.5px solid #e2e8f0",
                    borderTop: "4px solid #d4af37",
                    borderRadius: "14px",
                    padding: "24px",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.04)",
                    position: "relative"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px", borderBottom: "1px solid #f1f5f9", paddingBottom: "12px" }}>
                    <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "800", color: "#5d1d2b" }}>
                      Award Entry #{index + 1}
                    </h3>
                    <button
                      type="button"
                      onClick={() => handleDeleteAward(index, item)}
                      className="delete-btn"
                      style={{ padding: "6px 14px", fontSize: "13px" }}
                      title="Delete this award entry"
                    >
                      <i className="bi bi-trash me-1"></i> Delete Award
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: "16px", marginBottom: "16px" }}>
                    <div>
                      <label style={{ display: "block", fontSize: "13px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                        Award Title / Honor Name
                      </label>
                      <input
                        type="text"
                        value={item.title || ""}
                        onChange={(e) => {
                          const copy = [...awardList];
                          copy[index].title = e.target.value;
                          setAwardList(copy);
                        }}
                        placeholder="e.g. VEDA BHAASHYA RATNAM AWARD FROM THE AACHARYAS"
                        style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "14px", fontWeight: "600" }}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: "13px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                        Date / Period
                      </label>
                      <input
                        type="text"
                        value={item.date || ""}
                        onChange={(e) => {
                          const copy = [...awardList];
                          copy[index].date = e.target.value;
                          setAwardList(copy);
                        }}
                        placeholder="e.g. October – 2025."
                        style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "14px" }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
                      Award Narrative & Citation Content
                    </label>
                    <textarea
                      value={item.content || ""}
                      onChange={(e) => {
                        const copy = [...awardList];
                        copy[index].content = e.target.value;
                        setAwardList(copy);
                      }}
                      placeholder="Enter the full description, blessing context, citation, and details of the award..."
                      style={{ width: "100%", minHeight: "90px", padding: "12px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "14px", lineHeight: "1.6" }}
                    />
                  </div>

                  {/* 3 CEREMONY PHOTOS */}
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "700", color: "#475569", marginBottom: "12px" }}>
                      Award Ceremony Photos (Up to 3 high-res images)
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
                      {["image1", "image2", "image3"].map((field, photoIdx) => {
                        const imgPath = item[field];
                        return (
                          <div
                            key={field}
                            style={{
                              background: "#f8fafc",
                              border: "1.5px dashed #cbd5e1",
                              borderRadius: "12px",
                              padding: "14px",
                              textAlign: "center",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "180px",
                              position: "relative"
                            }}
                          >
                            <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", marginBottom: "8px" }}>
                              Photo #{photoIdx + 1}
                            </span>

                            {imgPath ? (
                              <div style={{ width: "100%", height: "140px", position: "relative" }}>
                                  <img
                                    src={getImageUrl(imgPath)}
                                    alt={`Award Ceremony ${photoIdx + 1}`}
                                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px", border: "1px solid #e2e8f0" }}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveAwardImage(index, field)}
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    right: "-8px",
                                    background: "#dc2626",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: "24px",
                                    height: "24px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px"
                                  }}
                                  title="Remove photo"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div>
                                <i className="bi bi-cloud-arrow-up" style={{ fontSize: "28px", color: "#94a3b8" }}></i>
                                <p style={{ fontSize: "12px", color: "#64748b", margin: "6px 0 10px 0" }}>Upload ceremony photo</p>
                                <label
                                  style={{
                                    background: "#5d1d2b",
                                    color: "#fff",
                                    padding: "6px 14px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                    display: "inline-block"
                                  }}
                                >
                                  Choose File
                                  <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={(e) => handleAwardImageUpload(e.target.files?.[0], index, field)}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bottom Save Action Bar */}
          {awardList.length > 0 && (
            <div style={{ marginTop: "30px", display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={handleSaveAwards}
                disabled={savingAwards}
                style={{
                  background: "linear-gradient(135deg, #5d1d2b 0%, #8b2738 100%)",
                  color: "#ffffff",
                  border: "2px solid #d4af37",
                  padding: "14px 36px",
                  borderRadius: "10px",
                  fontWeight: "800",
                  fontSize: "15px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  boxShadow: "0 6px 20px rgba(93, 29, 43, 0.3)"
                }}
              >
                {savingAwards ? (
                  <><span className="spinner-border spinner-border-sm" role="status"></span> Saving Awards...</>
                ) : (
                  <><i className="bi bi-check-circle-fill" style={{ color: "#ffd700" }}></i> Save All Awards & Changes</>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminEvents;
